import axios from "axios";
import firebase, { FS } from "./firebase";
import moment from "moment";
import { STATUS } from "../constants";

const getStore = () => firebase.modularFirestore();
export const searchPatientsService = async ({ searchText }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_CLIENTS}?search=${searchText}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const fetchPatientsService = async ({ pages }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_PATIENTS}?pages=${pages}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};
export const updateWorkflowNeedAttentionService = async (id, needAttention) => {
  try {
    const doc = FS.doc(getStore(), "client_appointments", id);
    await FS.setDoc(doc, { needAttention }, { merge: true });
    return { status: STATUS.SUCCEED };
  } catch (e) {
    return { status: STATUS.FAILED };
  }
};
export const fetchPatientDetailsService = async ({ clientId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_PATIENT}?clientId=${clientId}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const trackPatientService = async ({ clientId, referralId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_TRACK_PATIENT}`, {
    params: { clientId, ...(referralId ? { referralId } : {}) },
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const getReferredSpecialistsService = async ({ clientId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_API_HOST}/pms/admin/workflow/referred-specialists`, {
    params: { clientId },
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const checkPatientService = async ({ clientId }) => {
  const queryResult = await FS.getDocs(
    FS.query(
      FS.collection(firebase.modularFirestore(), "client_appointments"),
      FS.where("client.clientId", "==", clientId)
    )
  );

  const appointments = queryResult?.docs?.map((doc) => ({
    ...doc.data(),
    workflowId: doc.id,
  }));
  return appointments;
};

export const createPatientService = async (client) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_CREATE_PATIENT}`,
    { client },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const updatePatientService = async ({ id, clientId, client }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_CREATE_PATIENT}`,
    { id, clientId, client },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

/**
 * A blank category selection means "leave it alone", not "clear it" — mirroring
 * selectCategory, which ignores falsey values when writing Firestore. Without
 * this the two stores would drift: Firestore would keep the old category while
 * PMS took the mapper's default. The adapter also rejects "" outright, since
 * its category field is @IsNotEmpty.
 */
const normaliseCategory = (category) => category || undefined;

/**
 * Patches an existing PMS referral with the referral date and the WF category.
 *
 * No expectedAppointmentDate: that field is synced by the tasks endpoint, which
 * owns the Firestore write for it as well. Accepting it here would let a caller
 * move PMS while appointment_t.expected_appointment_date went stale.
 *
 * An omitted field leaves the stored value untouched.
 *
 * Renamed from updateReferralDateService (no longer date-only). The endpoint
 * path is unchanged so the API can deploy ahead of the UI.
 */
export const updateReferralDetailsService = async ({ id, referralId, referralDate, category }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  const body = { id, referralId };
  if (referralDate !== undefined) body.referralDate = referralDate;
  const normalisedCategory = normaliseCategory(category);
  if (normalisedCategory !== undefined) body.category = normalisedCategory;

  return axios.patch(`${process.env.REACT_APP_UPDATE_REFERRAL_DATE}`, body, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const updateReferralService = async ({
  id,
  referralId,
  referralDate,
  clinic,
  referrer,
  category,
  expectedAppointmentDate,
}) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  const body = { id, referralId, referralDate, clinic, referrer };
  const normalisedCategory = normaliseCategory(category);
  if (normalisedCategory !== undefined) body.category = normalisedCategory;
  if (expectedAppointmentDate !== undefined) body.expectedAppointmentDate = expectedAppointmentDate;

  return axios.patch(`${process.env.REACT_APP_UPDATE_REFERRAL}`, body, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const createAppointmentService = async ({ id, appointment, skipInitialSMS }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_CREATE_APPOINTMENT}`,
    {
      id,
      appointment,
      selectedSMSTemplateSetId: appointment.selectedSMSTemplateSetId,
      skipInitialSMS,
    },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const sendSMSService = async (data) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(`${process.env.REACT_APP_SEND_SMS}`, data, { headers: { Authorization: `Bearer ${userIdToken}` } });
};

export const sendEmailService = async (data) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(`${process.env.REACT_APP_SEND_EMAIL}`, data, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const createAppointmentNotesService = async ({ id, notes }) => {
  try {
    const doc = FS.doc(getStore(), "client_appointments", id);
    await FS.setDoc(doc, { letter_t: { notes } }, { merge: true });
  } catch (err) {}
};

export const informAppointmentDeletion = async (data) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(`${process.env.REACT_APP_EMAIL_APPOINTMENT_DELETION}`, data, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const deleteAppointmentService = async (requestBody) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.delete(`${process.env.REACT_APP_CREATE_APPOINTMENT}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
    data: requestBody,
  });
};

export const changeAppointmentService = async ({
  id,
  appointmentId,
  startDateTime,
  endDateTime,
  duration,
  skipInitialSMS,
  slotId,
  consultationType,
}) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_CREATE_APPOINTMENT}`,
    {
      id,
      appointmentId,
      startDateTime,
      endDateTime,
      duration,
      skipInitialSMS,
      ...(slotId ? { slotId } : {}),
      ...(consultationType ? { consultationType } : {}),
    },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const updateTasksService = async ({ id, tasks, workflowName = "WORKFLOW" }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_TASKS}`,
    { id, tasks, workflowName },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const trackAppointmentService = async ({ appointmentId, secretCode }) => {
  return axios.get(`${process.env.REACT_APP_TRACK_APPOINTMENT}`, {
    params: { id: appointmentId, secret: secretCode },
  });
};

export const getReferrerLocationsService = async ({ referrerId, clinicId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  const referrerIdQuery = `referrerId=${referrerId}`;
  const query = clinicId ? `${referrerIdQuery}&clinicId=${clinicId}` : referrerIdQuery;
  return axios.get(`${process.env.REACT_APP_REFERRER_LOCATION}?${query}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const updateTrackAppointmentService = async ({
  id,
  secret,
  nextSession,
  itemNumber,
  isLetterReady,
  testRequired,
  notes,
}) => {
  return axios.put(
    `${process.env.REACT_APP_TRACK_APPOINTMENT}`,
    {
      nextSession,
      itemNumber,
      isLetterReady,
      testRequired,
      notes,
    },
    { params: { id, secret } }
  );
};

export const archiveService = async ({ selectedCaIds }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_ARCHIVE}`,
    { selectedCaIds },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const unArchiveService = async ({ selectedCaIds }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_UN_ARCHIVE}`,
    { selectedCaIds },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const deleteWorkflowService = async ({ ids }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_API_HOST}/admin/workflow/delete`,
    { ids },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const returnToGPService = async ({ id }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_API_HOST}/notification/return-to-gp`,
    { id },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const rebuildService = async (id) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_REBUILD}?id=${id}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const batchRebuildService = async (ids) => {
  const rebuildCalls = ids.map((id) => rebuildService(id));
  return Promise.all(rebuildCalls);
};

export const updateSMSSent = async (activeCaId, result) => {
  const timestamp = moment().unix();
  await FS.setDoc(
    FS.doc(firebase.modularFirestore(), "client_appointments", activeCaId),
    {
      sms: {
        received: {
          [timestamp]: result,
        },
      },
    },
    { merge: true }
  );
};

export const updateSmsStateService = async (activeCaId, answer) => {
  // Once acting patient is confirmed, we need to update the smsState in firebase client_appointments collection, which is used to track the sms state of the patient.
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_API_HOST}/pms/admin/client-appointments/${activeCaId}/sms-state`,
    { answer },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const selectHospital = async (activeCaId, location) => {
  if (location) {
    const doc = FS.doc(getStore(), "client_appointments", activeCaId);
    await FS.setDoc(doc, { location }, { merge: true });
  }
};

export const selectCategory = async (activeCaId, category) => {
  if (category) {
    const doc = FS.doc(getStore(), "client_appointments", activeCaId);
    await FS.setDoc(doc, { category }, { merge: true });
  }
};

export const updateFinNo = async (activeCaId, finNo) => {
  const doc = FS.doc(getStore(), "client_appointments", activeCaId);
  await FS.setDoc(doc, { finNo }, { merge: true });
};

export const updateSnapformSent = async (activeCaId, answer) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.put(
    `${process.env.REACT_APP_API_HOST}/snapform/patient-submitted/${activeCaId}`,
    { answer },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const updateIvr = async (activeCaId, answer, { status }) => {
  const newIvr = { status };
  newIvr.reply = answer ? "1" : "2";
  console.log({ ivr: newIvr });
  const doc = FS.doc(getStore(), "client_appointments", activeCaId);
  await FS.setDoc(doc, { ivr: newIvr }, { merge: true });
};

export const incomingSmsService = async (from, body) => {
  const From = encodeURIComponent(from);
  const Body = encodeURIComponent(body);
  return axios.post(`${process.env.REACT_APP_INCOMING_SMS}`, `From=${From}&Body=${Body}`, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
};

export const batchDeleteService = async (ids) => {
  const batch = firebase.modularFirestoreBatch();
  ids.forEach((id) => {
    const doc = FS.doc(getStore(), "client_appointments", id);
    batch.delete(doc);
  });

  try {
    await batch.commit();
  } catch (err) {}
};

export const updateWorkflowStatusService = async (id, newStatus) => {
  try {
    const doc = FS.doc(getStore(), "client_appointments", id);
    await FS.setDoc(doc, { status: newStatus }, { merge: true });
    return { status: STATUS.SUCCEED };
  } catch (e) {
    return { status: STATUS.FAILED };
  }
};

export const getHistoryWorkflowsForClient = async (firstName, lastName, dob) => {
  try {
    const dateOfBirth = dob.trim();
    const query = FS.query(
      FS.collection(getStore(), "client_appointments"),
      FS.where("client.firstName", "==", firstName.trim()),
      FS.where("client.lastName", "==", lastName.trim()),
      FS.where("client.dateOfBirth", "in", [dateOfBirth, `${dateOfBirth}T00:00:00`])
    );
    const historyCas = await FS.getDocs(query);
    return { data: historyCas.docs.map((doc) => ({ ...doc.data(), id: doc.id })) };
  } catch (error) {
    return { error };
  }
};
